import { useEffect, useState } from 'react';

// Network Information API is experimental and not in the DOM lib types.
type NetworkInformation = {
  downlink?: number;
  downlinkMax?: number;
  effectiveType?: string;
  rtt?: number;
  saveData?: boolean;
  type?: string;
  addEventListener?: (...args: unknown[]) => void;
  removeEventListener?: (...args: unknown[]) => void;
};
type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
};

type NetworkState = {
  online?: boolean;
  previous?: boolean;
  since?: Date;
  downlink?: number;
  downlinkMax?: number;
  effectiveType?: string;
  rtt?: number;
  saveData?: boolean;
  type?: string;
};

/**
 * Attaches an event listener when the target supports it. Calls addEventListener with the forwarded arguments only when the object exists and exposes that method.
 *
 * @param obj - Event target to attach to, possibly undefined.
 * @param args - Arguments forwarded to addEventListener.
 * @returns Nothing.
 */
const on = (
  obj: { addEventListener?: (...args: unknown[]) => void } | undefined,
  ...args: unknown[]
) => {
  if (obj && obj.addEventListener) {
    obj.addEventListener(...args);
  }
};

/**
 * Detaches an event listener when the target supports it. Calls removeEventListener with the forwarded arguments only when the object exists and exposes that method.
 *
 * @param obj - Event target to detach from, possibly undefined.
 * @param args - Arguments forwarded to removeEventListener.
 * @returns Nothing.
 */
const off = (
  obj: { removeEventListener?: (...args: unknown[]) => void } | undefined,
  ...args: unknown[]
) => {
  if (obj && obj.removeEventListener) {
    obj.removeEventListener(...args);
  }
};

const isNavigator = typeof navigator !== 'undefined';
const nav = isNavigator ? (navigator as NavigatorWithConnection) : undefined;
const conn = nav && (nav.connection || nav.mozConnection || nav.webkitConnection);

/**
 * Snapshots the browser's current network condition. Reads the navigator online flag plus the connection object's downlink, downlinkMax, effectiveType, rtt, saveData and type, and stamps a new since date only when the online flag flipped.
 *
 * @param previousState - Previous snapshot used to detect a change and carry since forward.
 * @returns A network state snapshot including the previous online flag and when it last changed.
 */
const getConnectionState = (previousState?: NetworkState): NetworkState => {
  const online = nav?.onLine;
  const previousOnline = previousState?.online;

  return {
    online,
    previous: previousOnline,
    since: online !== previousOnline ? new Date() : previousState?.since,
    downlink: conn?.downlink,
    downlinkMax: conn?.downlinkMax,
    effectiveType: conn?.effectiveType,
    rtt: conn?.rtt,
    saveData: conn?.saveData,
    type: conn?.type,
  };
};

/**
 * Tracks the browser's online status and connection quality across renders. Seeds state from the supplied snapshot or a fresh reading, then subscribes to the window online and offline events plus the connection change event, detaching all three on unmount.
 *
 * @param initialState - Snapshot to seed state with instead of reading the connection.
 * @returns The current network state snapshot.
 */
const useNetworkState = (initialState?: NetworkState) => {
  const [state, setState] = useState(initialState ?? getConnectionState);

  useEffect(() => {
    /**
     * Re-reads the connection snapshot whenever the browser reports a network change. Passes the reader itself to the state setter so it runs against the previous snapshot.
     *
     * @returns Nothing.
     */
    const handleStateChange = () => {
      setState(getConnectionState);
    };

    on(window, 'online', handleStateChange, { passive: true });
    on(window, 'offline', handleStateChange, { passive: true });

    if (conn) {
      on(conn, 'change', handleStateChange, { passive: true });
    }

    return () => {
      off(window, 'online', handleStateChange);
      off(window, 'offline', handleStateChange);

      if (conn) {
        off(conn, 'change', handleStateChange);
      }
    };
  }, []);

  return state;
};

export default useNetworkState;
